import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import camposDinamicosApi from '../api/camposDinamicosApi';
import valoresCamposApi from '../api/valoresCamposApi';

const CamposDinamicosContext = createContext();

/**
 * Context para manejar campos dinámicos y sus valores
 * Proporciona acceso al catálogo de campos, asignaciones por estado y valores capturados
 */
export const CamposDinamicosProvider = ({ children }) => {
  const { user } = useAuth();

  // Estado para campos asignados por estado
  const [camposPorEstado, setCamposPorEstado] = useState({});

  // Estado para el catálogo completo de campos
  const [catalogoCampos, setCatalogoCampos] = useState([]);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingCatalogo, setLoadingCatalogo] = useState(false);

  // ========================================
  // CATÁLOGO DE CAMPOS
  // ========================================

  /**
   * Cargar catálogo completo de campos disponibles
   * @param {Object} params - Filtros opcionales
   */
  const cargarCatalogo = useCallback(async (params = {}) => {
    if (!user) return;

    try {
      setLoadingCatalogo(true);
      const response = await camposDinamicosApi.getCampos(params);
      setCatalogoCampos(response.data || []);
    } catch (err) {
      console.error('Error cargando catálogo de campos:', err);
      setCatalogoCampos([]);
    } finally {
      setLoadingCatalogo(false);
    }
  }, [user]);

  /**
   * Obtener detalle de un campo específico
   * @param {number} idCampo - ID del campo
   * @returns {Promise<Object|null>}
   */
  const obtenerCampo = useCallback(async (idCampo) => {
    try {
      const response = await camposDinamicosApi.getCampo(idCampo);
      return response.data;
    } catch (err) {
      console.error(`Error obteniendo campo ${idCampo}:`, err);
      return null;
    }
  }, []);

  /**
   * Crear nuevo campo en el catálogo
   * @param {Object} campoData - Datos del campo
   * @returns {Promise<Object>} { success: boolean, data?: Object, error?: string }
   */
  const crearCampo = useCallback(async (campoData) => {
    try {
      const response = await camposDinamicosApi.createCampo(campoData);
      await cargarCatalogo(); // Recargar catálogo
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Error creando campo:', err);
      return {
        success: false,
        error: err.response?.data?.error || err.message || 'Error al crear campo'
      };
    }
  }, [cargarCatalogo]);

  /**
   * Actualizar campo existente
   * @param {number} idCampo - ID del campo
   * @param {Object} campoData - Datos a actualizar
   * @returns {Promise<Object>} { success: boolean, data?: Object, error?: string }
   */
  const actualizarCampo = useCallback(async (idCampo, campoData) => {
    try {
      const response = await camposDinamicosApi.updateCampo(idCampo, campoData);
      await cargarCatalogo();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Error actualizando campo:', err);
      return {
        success: false,
        error: err.response?.data?.error || err.message || 'Error al actualizar campo'
      };
    }
  }, [cargarCatalogo]);

  /**
   * Desactivar campo (soft delete)
   * @param {number} idCampo - ID del campo
   * @returns {Promise<Object>} { success: boolean, error?: string }
   */
  const eliminarCampo = useCallback(async (idCampo) => {
    try {
      await camposDinamicosApi.deleteCampo(idCampo);
      await cargarCatalogo();
      return { success: true };
    } catch (err) {
      console.error('Error eliminando campo:', err);
      return {
        success: false,
        error: err.response?.data?.error || err.message || 'Error al eliminar campo'
      };
    }
  }, [cargarCatalogo]);

  // ========================================
  // ASIGNACIÓN DE CAMPOS A ESTADOS
  // ========================================

  /**
   * Cargar campos asignados a un estado específico
   * @param {number} idEstado - ID del estado
   * @returns {Promise<Array>}
   */
  const cargarCamposPorEstado = useCallback(async (idEstado) => {
    try {
      setLoading(true);
      const response = await camposDinamicosApi.getCamposPorEstado(idEstado);

      // Actualizar cache de campos por estado
      setCamposPorEstado(prev => ({
        ...prev,
        [idEstado]: response.data || []
      }));

      return response.data || [];
    } catch (err) {
      console.error(`Error cargando campos del estado ${idEstado}:`, err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cargar todos los campos por estado de la empresa
   * @returns {Promise<Object>}
   */
  const cargarTodosCampos = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const response = await camposDinamicosApi.getCamposPorEmpresa();
      setCamposPorEstado(response.data || {});
      return response.data || {};
    } catch (err) {
      console.error('Error cargando campos de empresa:', err);
      return {};
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Asignar un campo a un estado
   * @param {Object} asignacionData - Datos de asignación
   * @returns {Promise<Object>} { success: boolean, data?: Object, error?: string }
   */
  const asignarCampoAEstado = useCallback(async (asignacionData) => {
    try {
      const response = await camposDinamicosApi.asignarCampo(asignacionData);

      // Recargar campos del estado afectado
      if (asignacionData.id_estado) {
        await cargarCamposPorEstado(asignacionData.id_estado);
      }

      return { success: true, data: response.data };
    } catch (err) {
      console.error('Error asignando campo a estado:', err);
      return {
        success: false,
        error: err.response?.data?.error || err.message || 'Error al asignar campo'
      };
    }
  }, [cargarCamposPorEstado]);

  /**
   * Actualizar asignación de campo a estado
   * @param {number} idAsignacion - ID de la asignación
   * @param {Object} asignacionData - Datos a actualizar
   * @returns {Promise<Object>} { success: boolean, error?: string }
   */
  const actualizarAsignacion = useCallback(async (idAsignacion, asignacionData) => {
    try {
      await camposDinamicosApi.updateAsignacion(idAsignacion, asignacionData);
      // Recargar todos los campos para actualizar cache
      await cargarTodosCampos();
      return { success: true };
    } catch (err) {
      console.error('Error actualizando asignación:', err);
      return {
        success: false,
        error: err.response?.data?.error || err.message || 'Error al actualizar asignación'
      };
    }
  }, [cargarTodosCampos]);

  /**
   * Eliminar asignación de campo a estado
   * @param {number} idAsignacion - ID de la asignación
   * @param {number} idEstado - ID del estado (para recargar)
   * @returns {Promise<Object>} { success: boolean, error?: string }
   */
  const eliminarAsignacion = useCallback(async (idAsignacion, idEstado) => {
    try {
      await camposDinamicosApi.deleteAsignacion(idAsignacion);

      // Recargar campos del estado afectado
      if (idEstado) {
        await cargarCamposPorEstado(idEstado);
      }

      return { success: true };
    } catch (err) {
      console.error('Error eliminando asignación:', err);
      return {
        success: false,
        error: err.response?.data?.error || err.message || 'Error al eliminar asignación'
      };
    }
  }, [cargarCamposPorEstado]);

  // ========================================
  // VALORES DE CAMPOS EN ÓRDENES
  // ========================================

  /**
   * Obtener valores de campos de una orden
   * @param {number} idOrden - ID de la orden
   * @returns {Promise<Array>}
   */
  const getValoresOrden = useCallback(async (idOrden) => {
    try {
      const response = await valoresCamposApi.getValoresPorOrden(idOrden);
      return response.data || [];
    } catch (err) {
      console.error('Error obteniendo valores de orden:', err);
      return [];
    }
  }, []);

  /**
   * Guardar valor de un campo para una orden
   * @param {Object} valorData - Datos del valor
   * @returns {Promise<Object>} { success: boolean, data?: Object, error?: string }
   */
  const saveValorCampo = useCallback(async (valorData) => {
    try {
      const response = await valoresCamposApi.saveValor(valorData);
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Error guardando valor:', err);
      return {
        success: false,
        error: err.response?.data?.error || err.message || 'Error al guardar valor'
      };
    }
  }, []);

  /**
   * Guardar múltiples valores de campos a la vez
   * @param {number} idOrden - ID de la orden
   * @param {Array<Object>} valores - Array de valores a guardar
   * @returns {Promise<Object>} { success: boolean, data?: Object, error?: string }
   */
  const saveValoresBatch = useCallback(async (idOrden, valores) => {
    try {
      const response = await valoresCamposApi.saveValoresBatch({
        id_orden_servicio: idOrden,
        valores
      });
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Error guardando valores batch:', err);
      return {
        success: false,
        error: err.response?.data?.error || err.message || 'Error al guardar valores'
      };
    }
  }, []);

  /**
   * Validar que todos los campos obligatorios estén completos
   * @param {number} idOrden - ID de la orden
   * @param {number} idEstado - ID del estado a validar
   * @returns {Promise<Object>} { valido: boolean, camposFaltantes?: Array }
   */
  const validarCamposObligatorios = useCallback(async (idOrden, idEstado) => {
    try {
      const response = await valoresCamposApi.validarCamposObligatorios({
        id_orden_servicio: idOrden,
        id_estado: idEstado
      });
      return response.data || { valido: false };
    } catch (err) {
      console.error('Error validando campos obligatorios:', err);
      return {
        valido: false,
        error: err.response?.data?.error || err.message || 'Error al validar'
      };
    }
  }, []);

  // ========================================
  // HELPERS
  // ========================================

  /**
   * Obtener campos de un estado desde el cache
   * @param {number} idEstado - ID del estado
   * @returns {Array}
   */
  const getCamposEstado = useCallback((idEstado) => {
    return camposPorEstado[idEstado] || [];
  }, [camposPorEstado]);

  /**
   * Verificar si un estado tiene campos asignados
   * @param {number} idEstado - ID del estado
   * @returns {boolean}
   */
  const tieneCampos = useCallback((idEstado) => {
    const campos = camposPorEstado[idEstado];
    return campos && campos.length > 0;
  }, [camposPorEstado]);

  /**
   * Obtener solo campos obligatorios de un estado
   * @param {number} idEstado - ID del estado
   * @returns {Array}
   */
  const getCamposObligatorios = useCallback((idEstado) => {
    const campos = camposPorEstado[idEstado] || [];
    return campos.filter(campo => campo.es_obligatorio === true);
  }, [camposPorEstado]);

  // Cargar catálogo al iniciar si hay usuario
  useEffect(() => {
    if (user) {
      cargarCatalogo();
    }
  }, [user, cargarCatalogo]);

  const value = {
    // Estado
    camposPorEstado,
    catalogoCampos,
    loading,
    loadingCatalogo,

    // Catálogo
    cargarCatalogo,
    obtenerCampo,
    crearCampo,
    actualizarCampo,
    eliminarCampo,

    // Asignaciones
    cargarCamposPorEstado,
    cargarTodosCampos,
    asignarCampoAEstado,
    actualizarAsignacion,
    eliminarAsignacion,

    // Valores
    getValoresOrden,
    saveValorCampo,
    saveValoresBatch,
    validarCamposObligatorios,

    // Helpers
    getCamposEstado,
    tieneCampos,
    getCamposObligatorios
  };

  return (
    <CamposDinamicosContext.Provider value={value}>
      {children}
    </CamposDinamicosContext.Provider>
  );
};

/**
 * Hook para usar el contexto de campos dinámicos
 * @returns {Object} Context value con campos, valores, loading y funciones
 */
export const useCamposDinamicos = () => {
  const context = useContext(CamposDinamicosContext);
  if (!context) {
    throw new Error('useCamposDinamicos debe usarse dentro de CamposDinamicosProvider');
  }
  return context;
};
